import { useEffect, useState } from "react";
import { GlobalLoader } from "@/components/loading/GlobalLoader";

type Listener = (activeRequests: number) => void;
type NativeXhrOpen = typeof XMLHttpRequest.prototype.open;
type NativeXhrSend = typeof XMLHttpRequest.prototype.send;

const listeners = new Set<Listener>();
const trackedRequests = new WeakSet<XMLHttpRequest>();
let activeRequests = 0;
let installed = false;
let installCount = 0;
let originalFetch: typeof window.fetch | null = null;
let originalXhrOpen: NativeXhrOpen | null = null;
let originalXhrSend: NativeXhrSend | null = null;

function notifyListeners() {
  for (const listener of listeners) {
    listener(activeRequests);
  }
}

function startRequest() {
  activeRequests += 1;
  notifyListeners();
}

function finishRequest() {
  activeRequests = Math.max(0, activeRequests - 1);
  notifyListeners();
}

function getRequestUrl(input: RequestInfo | URL) {
  if (typeof input === "string") {
    return input;
  }

  if (input instanceof URL) {
    return input.href;
  }

  return input.url;
}

function shouldTrackRequest(method: string, url: string) {
  const normalizedMethod = method.toUpperCase();

  if (normalizedMethod === "GET" || normalizedMethod === "HEAD") {
    return false;
  }

  if (
    url.includes("/Listen/channel") ||
    url.includes("google.firestore.v1.Firestore/Listen") ||
    url.includes("/images/gifs/loader_") ||
    url.includes("/assets/")
  ) {
    return false;
  }

  return true;
}

function installTracker() {
  installCount += 1;

  if (installed) {
    return;
  }

  installed = true;
  originalFetch = window.fetch.bind(window);
  originalXhrOpen = Reflect.get(XMLHttpRequest.prototype, "open");
  originalXhrSend = Reflect.get(XMLHttpRequest.prototype, "send");
  const fetchImplementation = originalFetch;
  const xhrOpenImplementation = originalXhrOpen;
  const xhrSendImplementation = originalXhrSend;

  window.fetch = async (input, init) => {
    const method = init?.method ?? (input instanceof Request ? input.method : "GET");
    const url = getRequestUrl(input);
    const track = shouldTrackRequest(method, url);

    if (track) {
      startRequest();
    }

    try {
      return await fetchImplementation(input, init);
    } finally {
      if (track) {
        finishRequest();
      }
    }
  };

  const patchedOpen: NativeXhrOpen = function patchedOpen(
    this: XMLHttpRequest,
    method: string,
    url: string | URL,
    async?: boolean,
    username?: string | null,
    password?: string | null
  ) {
    if (shouldTrackRequest(method, String(url))) {
      trackedRequests.add(this);
    }

    if (typeof async === "boolean") {
      return xhrOpenImplementation.call(this, method, url, async, username, password);
    }

    return xhrOpenImplementation.call(this, method, url, true);
  };

  const patchedSend: NativeXhrSend = function patchedSend(
    this: XMLHttpRequest,
    body?: Document | XMLHttpRequestBodyInit | null
  ) {
    const track = trackedRequests.has(this);

    if (track) {
      startRequest();

      const finish = () => {
        this.removeEventListener("loadend", finish);
        this.removeEventListener("error", finish);
        this.removeEventListener("abort", finish);
        trackedRequests.delete(this);
        finishRequest();
      };

      this.addEventListener("loadend", finish);
      this.addEventListener("error", finish);
      this.addEventListener("abort", finish);
    }

    return xhrSendImplementation.call(this, body);
  };

  XMLHttpRequest.prototype.open = patchedOpen;
  XMLHttpRequest.prototype.send = patchedSend;
}

function uninstallTracker() {
  installCount = Math.max(0, installCount - 1);

  if (installCount > 0 || !installed) {
    return;
  }

  if (originalFetch) {
    window.fetch = originalFetch;
  }

  if (originalXhrOpen) {
    XMLHttpRequest.prototype.open = originalXhrOpen;
  }

  if (originalXhrSend) {
    XMLHttpRequest.prototype.send = originalXhrSend;
  }

  installed = false;
  originalFetch = null;
  originalXhrOpen = null;
  originalXhrSend = null;
  activeRequests = 0;
  notifyListeners();
}

export function GlobalRequestLoader() {
  const [active, setActive] = useState(activeRequests);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    installTracker();
    const listener: Listener = (nextActive) => {
      setActive(nextActive);

      if (nextActive <= 0) {
        setVisible(false);
      }
    };

    listeners.add(listener);

    return () => {
      listeners.delete(listener);
      uninstallTracker();
    };
  }, []);

  useEffect(() => {
    if (active <= 0) {
      return;
    }

    const timeout = window.setTimeout(() => setVisible(true), 180);

    return () => window.clearTimeout(timeout);
  }, [active]);

  return active > 0 && visible ? <GlobalLoader label="Processando solicitação" /> : null;
}
